import { useState, useEffect, useRef } from 'react';
import type { Deal, TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { dedupeById, sumUniqueBy, isAged, ageSeverity, formatAge, splitSCNames } from '../../utils/deals';
import { MetricCard } from '../ui/MetricCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { OfferingChart } from '../charts/OfferingChart';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { useDeals } from '../../context/DealsContext';
import React from 'react';
interface Props {
  state: TabState;
  searchRows?: React.ReactNode;
}
function regionBadge(region: string) {
  const styles: Record<string, {
    bg: string;
    color: string;
  }> = {
    AMER: {
      bg: '#EFF6FF',
      color: '#1D4ED8'
    },
    EMEA: {
      bg: '#F5F3FF',
      color: '#7C3AED'
    },
    JAPAC: {
      bg: '#F0FDF4',
      color: '#15803D'
    }
  };
  const s = styles[region] || {
    bg: '#F9FAFB',
    color: '#374151'
  };
  return <span className="badge" style={{
    background: s.bg,
    color: s.color,
    fontSize: 8
  }} data-id="element-576" data-mp="1706al35vnjal">{region}</span>;
}
export function SDPanel({
  state,
  searchRows
}: Props) {
  const {
    deals
  } = useDeals();
  const all = getFiltered('sd', state, deals);
  const ip = all.filter(d => d.status === 'In Progress');
  const aged = ip.filter(d => isAged(d));
  const scs = new Set(ip.flatMap(d => splitSCNames(d.sc))).size;
  const oppGnarr = sumUniqueBy(all, d => d.opp_gnarr);

  // Closed Booked GNARR: won deals matching region/sub and close_q quarter filter
  const wonDeals = dedupeById(deals.filter(d => {
    if (d.adjusted_commit !== 'Won') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (state.q !== 'All' || state.fy !== 'All') {
      if (!d.close_q) return false;
      const [yr, qt] = d.close_q.split('-');
      if (state.fy !== 'All' && yr !== state.fy) return false;
      if (state.q !== 'All' && qt !== state.q) return false;
    }
    return true;
  }));
  const wonGnarr = wonDeals.reduce((s, d) => s + (d.opp_gnarr || 0), 0);
  const wonCount = wonDeals.length;
  const agedTop8 = aged.slice(0, 8);

  // Merge deals by id: combine SC names, keep first occurrence's other fields
  interface MergedDeal extends Deal {
    mergedSc: string;
    displayStatus: string;
  }
  const dealMap = new Map<string, MergedDeal>();
  for (const d of all) {
    const existing = dealMap.get(d.id);
    if (existing) {
      const scNames = new Set([...existing.mergedSc.split(',').map(s => s.trim()).filter(Boolean), ...d.sc.split(',').map(s => s.trim()).filter(Boolean)]);
      existing.mergedSc = [...scNames].join(', ');
      if (!existing.displayStatus && d.opp_status) existing.displayStatus = d.opp_status;
    } else {
      dealMap.set(d.id, {
        ...d,
        mergedSc: d.sc,
        displayStatus: d.opp_status
      });
    }
  }
  const mergedDeals = [...dealMap.values()];
  const [searchId, setSearchId] = useState('');
  const [searchAccount, setSearchAccount] = useState('');
  const [searchSc, setSearchSc] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Derived ranges from data
  const ages = mergedDeals.map(d => d.age ?? 0);
  const gnarrs = mergedDeals.map(d => d.gnarr ?? 0);
  const dataAgeMin = Math.min(...ages);
  const dataAgeMax = Math.max(...ages);
  const dataGnarrMin = Math.min(...gnarrs);
  const dataGnarrMax = Math.max(...gnarrs);
  const [ageMin, setAgeMin] = useState(dataAgeMin);
  const [ageMax, setAgeMax] = useState(dataAgeMax);
  const [gnarrMin, setGnarrMin] = useState(dataGnarrMin);
  const [gnarrMax, setGnarrMax] = useState(dataGnarrMax);
  const [selectedCloseQs, setSelectedCloseQs] = useState<string[]>([]);
  const [closeQDropdownOpen, setCloseQDropdownOpen] = useState(false);
  const closeQDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!statusDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusDropdownOpen]);
  useEffect(() => {
    if (!closeQDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (closeQDropdownRef.current && !closeQDropdownRef.current.contains(e.target as Node)) {
        setCloseQDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [closeQDropdownOpen]);
  const allStatuses = [...new Set(mergedDeals.map(d => d.displayStatus).filter(Boolean))].sort();
  const allCloseQs = [...new Set(mergedDeals.map(d => d.close_q).filter(Boolean))].sort();
  const mergedFiltered = mergedDeals.filter(d => {
    if (searchId && !d.id.toLowerCase().includes(searchId.toLowerCase())) return false;
    if (searchAccount && !d.account.toLowerCase().includes(searchAccount.toLowerCase())) return false;
    if (searchSc && !d.mergedSc.toLowerCase().includes(searchSc.toLowerCase())) return false;
    if (selectedStatuses.length > 0 && !selectedStatuses.includes(d.displayStatus)) return false;
    if (d.age !== null && (d.age < ageMin || d.age > ageMax)) return false;
    if (d.gnarr !== null && (d.gnarr < gnarrMin || d.gnarr > gnarrMax)) return false;
    if (selectedCloseQs.length > 0 && !selectedCloseQs.includes(d.close_q)) return false;
    return true;
  });
  const STAGE_COLORS: Record<string, {
    bg: string;
    fg: string;
  }> = {
    'Prospect': {
      bg: '#F3F4F6',
      fg: '#6B7280'
    },
    'Qualification': {
      bg: '#EFF6FF',
      fg: '#3B82F6'
    },
    'Influence': {
      bg: '#F0F9FF',
      fg: '#0EA5E9'
    },
    'Solutioning': {
      bg: '#F5F3FF',
      fg: '#8B5CF6'
    },
    'Customer Commit': {
      bg: '#FFF7ED',
      fg: '#F97316'
    },
    'Order': {
      bg: '#FFFBEB',
      fg: '#D97706'
    },
    'Closed - Booked': {
      bg: '#F0FDF4',
      fg: '#16A34A'
    }
  };
  const statusBadge = (status: string) => {
    const c = STAGE_COLORS[status] || {
      bg: '#F9FAFB',
      fg: '#374151'
    };
    return <span className="badge" style={{
      background: c.bg,
      color: c.fg,
      fontSize: 8
    }} data-id="element-577" data-mp="3k51l3w7ullfu">{status}</span>;
  };
  const mergedColumns: SortableColumn<MergedDeal>[] = [{
    key: 'id',
    label: 'DR ID',
    width: '8%',
    sortValue: d => d.id,
    render: d => <span style={{
      fontSize: 9,
      color: '#8892a0'
    }} data-id="element-578" data-mp="3t6slcljmd02b">{d.id}</span>
  }, {
    key: 'account',
    label: 'Account',
    width: '18%',
    sortValue: d => d.account,
    render: d => <span title={d.account} data-id="element-579" data-mp="18fid4m8c81dc">{d.account.substring(0, 24)}</span>
  }, {
    key: 'mergedSc',
    label: 'SC Assigned',
    width: '14%',
    sortValue: d => d.mergedSc,
    render: d => <span title={d.mergedSc} data-id="element-580" data-mp="2sx1a1um4l61f">{d.mergedSc.split(',').slice(0, 2).join(', ')}</span>
  }, {
    key: 'displayStatus',
    label: 'Status',
    width: '9%',
    sortValue: d => d.displayStatus,
    render: d => statusBadge(d.displayStatus)
  }, {
    key: 'offering',
    label: 'Offering',
    width: '12%',
    sortValue: d => d.offering,
    render: d => <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-581" data-mp="2f4bpzvq5qb6y">
        {d.offering.replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
      </span>
  }, {
    key: 'age',
    label: 'Age',
    width: '6%',
    sortValue: d => d.age ?? 0,
    render: d => <span className={ageSeverity(d.age) === 'high' ? 'c-red' : ageSeverity(d.age) === 'medium' ? 'c-gold' : ''} style={{
      fontWeight: ageSeverity(d.age) === 'high' ? 600 : 400
    }} data-id="element-582" data-mp="217ep11gbrdcc">
        {formatAge(d.age)}
      </span>
  }, {
    key: 'gnarr',
    label: 'GNARR',
    width: '9%',
    sortValue: d => d.gnarr,
    render: d => <>{fmtG(d.gnarr)}</>
  }, {
    key: 'close_q',
    label: 'Close Q',
    width: '8%',
    sortValue: d => d.close_q,
    render: d => <>{d.close_q}</>
  }, {
    key: 'region',
    label: 'Region',
    width: '7%',
    sortValue: d => d.region,
    render: d => regionBadge(d.region)
  }, {
    key: 'sub_region',
    label: 'Sub',
    width: '5%',
    sortValue: d => d.sub_region,
    render: d => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-583" data-mp="2pobdy2se35sy">{d.sub_region}</span>
  }];
  const agedColumns: SortableColumn<Deal>[] = [{
    key: 'account',
    label: 'Account',
    width: '30%',
    sortValue: d => d.account,
    render: d => <span title={d.account} data-id="element-584" data-mp="2krks0ytwlfzm">{d.account.substring(0, 22)}</span>
  }, {
    key: 'sc',
    label: 'SC',
    width: '20%',
    sortValue: d => d.sc,
    render: d => <span title={d.sc} data-id="element-585" data-mp="26suwhnmqfodd">{d.sc.split(' ')[0]}</span>
  }, {
    key: 'age',
    label: 'Age',
    width: '12%',
    sortValue: d => d.age ?? 0,
    render: d => <span className="c-red" style={{
      fontWeight: 600
    }} data-id="element-586" data-mp="4zx4tv4oz1u0">{formatAge(d.age)}</span>
  }, {
    key: 'gnarr',
    label: 'GNARR',
    width: '18%',
    sortValue: d => d.gnarr,
    render: d => <>{fmtG(d.gnarr)}</>
  }, {
    key: 'close_q',
    label: 'Close Q',
    width: '12%',
    sortValue: d => d.close_q,
    render: d => <>{d.close_q}</>
  }, {
    key: 'sub_region',
    label: 'Sub',
    width: '8%',
    sortValue: d => d.sub_region,
    render: d => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-587" data-mp="246vkbw7ux5mp">{d.sub_region}</span>
  }];
  return <div data-id="element-588" data-mp="1zg59w4kkqcl5">
      {/* Metrics */}
      <div className="mrow" data-id="element-589" data-mp="1zf40e4j7hy5j">
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-590" data-mp="1zeuasi22pt0d" /> 
        <MetricCard label="Aged >90 days" value={aged.length} sub="need immediate action" colorClass="c-red" data-id="element-591" data-mp="2d9d5067v5q7l" />
        <MetricCard label="Closed Booked GNARR" value={fmtG(wonGnarr)} sub={`${wonCount} won opportunit${wonCount === 1 ? 'y' : 'ies'}`} colorClass="c-purple" data-id="element-592" data-mp="2n5pvkv843ny4" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-593" data-mp="2i7682qdve43n" />
      </div>

      {/* Offering chart + Aged deals */}
      <div className="row2" data-id="element-594" data-mp="stt3d01uie20">
        <div className="card" data-id="element-595" data-mp="nt5bkc5slt6q">
          <h3 data-id="element-596" data-mp="ntx75mze3qx0">Requests by solution</h3>
          <OfferingChart deals={all} data-id="element-597" data-mp="nuyizto8chfs" />
        </div>
        <div className="card" data-id="element-598" data-mp="5f24cppp1o0z">
          <h3 data-id="element-599" data-mp="5fbvigmh6189">Aged deals (&gt;90 days) at risk</h3>
          {agedTop8.length > 0 ? <SortableTable columns={agedColumns} data={agedTop8} rowKey={d => `${d.id}`} defaultSort={{
          key: 'age',
          dir: 'desc'
        }} data-id="element-600" data-mp="dcwfixrpqh4" /> : <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#8892a0',
          fontSize: 11
        }} data-id="element-601" data-mp="3mo8r1873nxm2">No aged deals in this filter ✓</div>}
        </div>
      </div>

      {/* Requests — full list */}
      <SectionHeader title="Requests — full list" data-id="element-602" data-mp="j7ru958cfd3d" />
      <div style={{
      padding: '8px 18px 0',
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }} data-id="element-603" data-mp="ednmijr97118">
        <input type="text" placeholder="Search DR ID…" value={searchId} onChange={e => setSearchId(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 120,
        color: '#344054',
        background: '#fff'
      }} data-id="element-604" data-mp="9ekr7r12s96g" />
        <input type="text" placeholder="Search Account…" value={searchAccount} onChange={e => setSearchAccount(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-605" data-mp="1ee985e6gegte" />
        <input type="text" placeholder="Search SC…" value={searchSc} onChange={e => setSearchSc(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-606" data-mp="1t9yxnmaulc2v" />
        <div ref={statusDropdownRef} style={{
        position: 'relative'
      }} data-id="element-607" data-mp="2c3kqod60yj9m">
          <button onClick={() => setStatusDropdownOpen(!statusDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-608" data-mp="2h0kv6jn87dym">
            {selectedStatuses.length > 0 ? `Status (${selectedStatuses.length})` : 'Status ▾'}
          </button>
          {statusDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 160,
          padding: '4px 0'
        }} data-id="element-609" data-mp="2a259o7110fbp">
              <div onClick={() => setSelectedStatuses(selectedStatuses.length === allStatuses.length ? [] : [...allStatuses])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-610" data-mp="2f449j6xk4u3w">
                {selectedStatuses.length === allStatuses.length ? 'Deselect all' : 'Select all'}
              </div>
              {allStatuses.map(s => <label key={s} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-611" data-mp="2mmlq1x3up96d">
                  <input type="checkbox" checked={selectedStatuses.includes(s)} onChange={() => {
              setSelectedStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
            }} data-id="element-612" data-mp="2dk2pjjnjf8na" />
                  {s}
                </label>)}
            </div>}
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-613" data-mp="6vppheq9diew">
          <span data-id="element-614" data-mp="22ykqj5oevpip">Age</span>
          <input type="range" min={dataAgeMin} max={dataAgeMax} value={ageMin} onChange={e => setAgeMin(Number(e.target.value))} style={{
          width: 60,
          accentColor: '#3B82F6'
        }} data-id="element-615" data-mp="22x15z2vq2sb4" />
          <input type="range" min={dataAgeMin} max={dataAgeMax} value={ageMax} onChange={e => setAgeMax(Number(e.target.value))} style={{
          width: 60,
          accentColor: '#3B82F6'
        }} data-id="element-616" data-mp="1tvtb6usf09ls" />
          <span style={{
          fontSize: 9,
          color: '#8892a0'
        }} data-id="element-617" data-mp="1k1jifbzk99aj">{ageMin}–{ageMax}d</span>
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-618" data-mp="1f7f7khn6ch46">
          <span data-id="element-619" data-mp="1a7klcrecgfpg">GNARR</span>
          <input type="range" min={dataGnarrMin} max={dataGnarrMax} value={gnarrMin} onChange={e => setGnarrMin(Number(e.target.value))} style={{
          width: 60,
          accentColor: '#3B82F6'
        }} data-id="element-620" data-mp="168bsrpv1invh" />
          <input type="range" min={dataGnarrMin} max={dataGnarrMax} value={gnarrMax} onChange={e => setGnarrMax(Number(e.target.value))} style={{
          width: 60,
          accentColor: '#3B82F6'
        }} data-id="element-621" data-mp="w9m8xrjl2tcl" />
          <span style={{
          fontSize: 9,
          color: '#8892a0'
        }} data-id="element-622" data-mp="3cv0yb571niie">${(gnarrMin / 1e6).toFixed(0)}M–${(gnarrMax / 1e6).toFixed(0)}M</span>
        </div>
        <div ref={closeQDropdownRef} style={{
        position: 'relative'
      }} data-id="element-623" data-mp="3htkppl4epmi5">
          <button onClick={() => setCloseQDropdownOpen(!closeQDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-624" data-mp="3lwh1oszfykd4">
            {selectedCloseQs.length > 0 ? `Close Q (${selectedCloseQs.length})` : 'Close Q ▾'}
          </button>
          {closeQDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 140,
          padding: '4px 0'
        }} data-id="element-625" data-mp="2ahtzixt09m78">
              <div onClick={() => setSelectedCloseQs(selectedCloseQs.length === allCloseQs.length ? [] : [...allCloseQs])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-626" data-mp="38rttemlmuy2j">
                {selectedCloseQs.length === allCloseQs.length ? 'Deselect all' : 'Select all'}
              </div>
              {allCloseQs.map(q => <label key={q} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-627" data-mp="33vmngtt8lmkl">
                  <input type="checkbox" checked={selectedCloseQs.includes(q)} onChange={() => {
              setSelectedCloseQs(prev => prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]);
            }} data-id="element-628" data-mp="j670k9x52i08" />
                  {q}
                </label>)}
            </div>}
        </div>
      </div>
      <div className="tw" data-id="element-629" data-mp="hnp510ixidwh">
        {searchRows ? <table className="tbl" data-id="element-630" data-mp="15d19jn3b9xsg">
            <thead data-id="element-631" data-mp="15e2mxy7suwdc">
              <tr data-id="element-632" data-mp="15djfjbfrzqdk">
                {mergedColumns.map(col => <th key={col.key} style={{
              width: col.width
            }} data-id="element-633" data-mp="1aat5cs72u41x">{col.label}</th>)}
              </tr>
            </thead>
            <tbody data-id="element-634" data-mp="vkvn0svtw8kn">{searchRows}</tbody>
          </table> : <SortableTable columns={mergedColumns} data={mergedFiltered} rowKey={d => d.id} data-id="element-635" data-mp="10kqcqt3vas44" />}
      </div>

    </div>;
}